// Admin Settings → AI Learning. Restricted-access page (nav item only shown
// to rex@ethx.com.au; the API endpoints used here are admin-only).
//
// Drop in reference photos named "{garment-name}-{location}.png". The page
// parses each filename, matches the garment by name and the print location
// by suffix, uploads the file via /api/files (kind='mockup'), then PATCHes
// the matching printLocations[i].mockupFileId. Per-file status is reported.
//
// This is the "free mockup" route — the studio supplies real photos for each
// garment/location and the system binds them in bulk. The page name nods at
// where this dataset will eventually feed an AI model for auto-generation.
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'
import { useToast, ToastBanner } from '../../components/Toast'

interface AdminGarment {
  id: string
  slug: string
  name: string
  aiPrompt: string | null
  printLocations: Array<{ id: string; name: string; left: number; top: number; width: number; height: number; sortOrder: number; mockupFileId: string | null; mockupUrl: string | null; aiReferenceFileId: string | null; aiReferenceUrl: string | null }>
}
interface AiRef {
  id: string
  originalName: string
  url: string
  productId: string | null
  productName: string | null
  productSlug: string | null
  widthPx: number | null
  heightPx: number | null
  createdAt: string
}
interface AdminGarmentResp { item: AdminGarment & { mockupUrl: string | null; description: string | null; isActive: boolean; sortOrder: number } }

interface PendingFile {
  file: File
  // parsed
  garmentGuess: string
  locationGuess: string
  matchedGarment: AdminGarment | null
  matchedLocationId: string | null
  // outcome
  status: 'pending' | 'uploading' | 'bound' | 'skipped' | 'error'
  error?: string
  previewUrl?: string
}

// Match logic — split filename on the last "-", treat the left side as the
// garment name (case-insensitive, allowing spaces) and the right side as the
// location suffix ("front", "back", "left-sleeve" → matches "Left Sleeve").
function parseFilename(file: File): { garmentName: string; locationName: string } {
  const base = file.name.replace(/\.[^.]+$/, '') // strip extension
  const dash = base.lastIndexOf('-')
  if (dash < 0) return { garmentName: base.trim(), locationName: 'front' }
  return {
    garmentName: base.slice(0, dash).trim(),
    locationName: base.slice(dash + 1).trim().replace(/-/g, ' '),
  }
}
function norm(s: string): string { return s.toLowerCase().replace(/\s+/g, ' ').trim() }

export default function AiLearning() {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const { toast, show } = useToast()
  const [garments, setGarments] = useState<AdminGarment[]>([])
  const [garmentsLoaded, setGarmentsLoaded] = useState(false)
  const [pending, setPending] = useState<PendingFile[]>([])
  const [processing, setProcessing] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [aiRefs, setAiRefs] = useState<AiRef[]>([])
  const [aiRefsLoaded, setAiRefsLoaded] = useState(false)

  // Load garments (with their print locations) on mount so we can match.
  useEffect(() => {
    (async () => {
      try {
        const list = await apiFetch<{ items: Array<{ id: string }> }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/garments')
        const details = await Promise.all(
          list.items.map((g) => apiFetch<AdminGarmentResp>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${g.id}`)),
        )
        setGarments(details.map((d) => ({
          id: d.item.id, slug: d.item.slug, name: d.item.name,
          aiPrompt: (d.item as AdminGarment & { aiPrompt?: string | null }).aiPrompt ?? null,
          printLocations: d.item.printLocations ?? [],
        })))
      } catch (e) { show((e as Error).message, 'error', 5000) }
      finally { setGarmentsLoaded(true) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load AI references (auto-labelled by the garment they came from).
  async function loadAiRefs() {
    try {
      const d = await apiFetch<{ items: AiRef[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/ai-references')
      setAiRefs(d.items)
    } catch { /* silent */ }
    finally { setAiRefsLoaded(true) }
  }
  useEffect(() => { void loadAiRefs() /* eslint-disable-next-line */ }, [])

  async function removeAiRef(id: string) {
    if (!confirm('Remove this reference?')) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/ai-references/${id}`, { method: 'DELETE' })
      setAiRefs((prev) => prev.filter((x) => x.id !== id))
    } catch (e) { show((e as Error).message, 'error', 5000) }
  }

  function stageFiles(files: FileList | null) {
    if (!files) return
    const next: PendingFile[] = Array.from(files).map((file) => {
      const { garmentName, locationName } = parseFilename(file)
      const matched = garments.find((g) => norm(g.name) === norm(garmentName) || norm(g.slug) === norm(garmentName))
      const loc = matched?.printLocations.find((l) => norm(l.name) === norm(locationName)) ?? null
      return {
        file,
        garmentGuess: garmentName,
        locationGuess: locationName,
        matchedGarment: matched ?? null,
        matchedLocationId: loc?.id ?? null,
        status: 'pending',
        previewUrl: URL.createObjectURL(file),
      }
    })
    setPending((prev) => [...prev, ...next])
  }

  async function processAll() {
    setProcessing(true)
    // Re-fetch one garment at a time as we PATCH so we don't clobber prior
    // location updates when binding multiple files for the same garment.
    const garmentState = new Map(garments.map((g) => [g.id, JSON.parse(JSON.stringify(g.printLocations)) as AdminGarment['printLocations']]))
    let ok = 0, skipped = 0, errored = 0

    for (let i = 0; i < pending.length; i++) {
      const item = pending[i]!
      if (item.status === 'bound' || item.status === 'skipped') continue
      if (!item.matchedGarment || !item.matchedLocationId) {
        setPending((prev) => prev.map((x, j) => j === i ? { ...x, status: 'skipped', error: !item.matchedGarment ? 'No matching garment' : 'No matching location' } : x))
        skipped++
        continue
      }
      setPending((prev) => prev.map((x, j) => j === i ? { ...x, status: 'uploading' } : x))
      try {
        // Read file → base64
        const dataUrl: string = await new Promise((res, rej) => {
          const r = new FileReader()
          r.onload = () => res(r.result as string)
          r.onerror = () => rej(r.error)
          r.readAsDataURL(item.file)
        })
        const up = await apiFetch<{ id: string; url: string }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
          method: 'POST',
          body: JSON.stringify({ originalName: item.file.name, mimeType: item.file.type, base64: dataUrl, kind: 'ai_reference', productId: item.matchedGarment.id }),
        })
        // Update the in-memory location list with the new mockupFileId.
        const locs = garmentState.get(item.matchedGarment.id) ?? item.matchedGarment.printLocations
        const updatedLocs = locs.map((l) => l.id === item.matchedLocationId ? { ...l, aiReferenceFileId: up.id } : l)
        garmentState.set(item.matchedGarment.id, updatedLocs)
        // Strip mockupUrl (computed server-side) before PATCH.
        const payload = updatedLocs.map(({ mockupUrl, aiReferenceUrl, ...rest }) => { void mockupUrl; void aiReferenceUrl; return rest })
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${item.matchedGarment.id}`, {
          method: 'PATCH', body: JSON.stringify({ printLocations: payload }),
        })
        setPending((prev) => prev.map((x, j) => j === i ? { ...x, status: 'bound' } : x))
        ok++
      } catch (e) {
        setPending((prev) => prev.map((x, j) => j === i ? { ...x, status: 'error', error: (e as Error).message } : x))
        errored++
      }
    }
    setProcessing(false)
    show(`Bound ${ok} mockup${ok === 1 ? '' : 's'}${skipped ? `, skipped ${skipped}` : ''}${errored ? `, ${errored} error${errored === 1 ? '' : 's'}` : ''}.`, errored ? 'error' : 'success', 5000)
  }

  function clearAll() {
    pending.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl))
    setPending([])
  }

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <div className="mb-6">
        <div className="eyebrow">Settings</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">AI Learning</h1>
        <p className="text-ink-2 text-sm mt-1 max-w-3xl">Bulk-upload reference photos for every garment + location. Name files <span className="font-mono text-xs">{`{garment name}-{location}.png`}</span> — for example <span className="font-mono text-xs">Crew Neck T-Shirt-front.png</span> or <span className="font-mono text-xs">Hat-back.jpg</span>. The page matches each file to its garment and location and binds the mockup. This dataset doubles as training material for future AI mockup generation.</p>
      </div>

      <div className="card mb-6">
        <div className="eyebrow">Catalogue snapshot</div>
        {!garmentsLoaded ? <div className="text-sm text-ink-3">Loading garments…</div> : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {garments.map((g) => (
              <div key={g.id} className="flex items-baseline justify-between border-b border-border py-1">
                <span className="text-ink font-semibold">{g.name}</span>
                <span className="text-ink-3">{g.printLocations.map((l) => l.name).join(' · ') || 'no locations'}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-ink-3 mt-2">Filenames must match garment name (case-insensitive) and a configured print location.</p>
      </div>

      <div className="card mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="eyebrow !mb-0">Drop reference photos</div>
            <p className="text-[11px] text-ink-3 mt-1">PNG / JPG. Multi-select; the page parses each filename.</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => stageFiles(e.target.files)} />
            <button onClick={() => fileRef.current?.click()} disabled={processing} className="btn-outline text-xs">+ Add files</button>
            {pending.length > 0 && <button onClick={clearAll} disabled={processing} className="text-xs text-ink-3 hover:text-ink">Clear</button>}
            {pending.length > 0 && <button onClick={processAll} disabled={processing} className="btn-gold">{processing ? 'Processing…' : `Process ${pending.length} file${pending.length === 1 ? '' : 's'}`}</button>}
          </div>
        </div>

        {pending.length === 0 ? (
          <div className="text-center py-12 text-ink-3 text-sm border-2 border-dashed border-border rounded">
            Drop files or click <strong className="text-ink">+ Add files</strong>. Each file is previewed + matched here before anything's uploaded.
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((p, i) => (
              <div key={i} className={'flex items-center gap-3 p-3 rounded border ' + (
                p.status === 'bound' ? 'border-emerald-300 bg-emerald-50' :
                p.status === 'skipped' ? 'border-amber-300 bg-amber-50' :
                p.status === 'error' ? 'border-red-300 bg-red-50' :
                p.status === 'uploading' ? 'border-gold bg-gold/5' :
                'border-border bg-white'
              )}>
                <div className="w-12 h-12 bg-white border border-border rounded overflow-hidden flex items-center justify-center shrink-0">
                  {p.previewUrl ? <img src={p.previewUrl} alt={p.file.name} className="max-w-full max-h-full object-contain" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink font-mono truncate">{p.file.name}</div>
                  <div className="text-[11px] text-ink-3 mt-0.5">
                    {p.matchedGarment ? (
                      <span><span className="text-ink-2 font-semibold">{p.matchedGarment.name}</span>{' · '}
                      {p.matchedLocationId ? <span className="text-ink-2">{p.matchedGarment.printLocations.find((l) => l.id === p.matchedLocationId)?.name}</span> : <span className="text-red-700">no matching location "{p.locationGuess}"</span>}</span>
                    ) : (
                      <span className="text-red-700">no garment matched for "{p.garmentGuess}"</span>
                    )}
                  </div>
                </div>
                <div className="text-xs shrink-0">
                  {p.status === 'pending'   && <span className="text-ink-3">queued</span>}
                  {p.status === 'uploading' && <span className="text-gold-dark">uploading…</span>}
                  {p.status === 'bound'     && <span className="text-emerald-700 font-bold">✓ bound</span>}
                  {p.status === 'skipped'   && <span className="text-amber-800" title={p.error}>skipped</span>}
                  {p.status === 'error'     && <span className="text-red-700" title={p.error}>error</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-garment AI prompts — admin-written guidance appended to the
          realism prompt sent to the image model. Save per garment. */}
      {garmentsLoaded && (
        <div className="card mb-6">
          <div className="eyebrow">Garment prompts</div>
          <p className="text-[11px] text-ink-3 mt-1 mb-4">Scene description the AI generates from when the customer presses Preview on this garment. Describe the photo exactly as you want it — garment cut, colour, material; the model (or "no model — flat lay"); pose, lighting, backdrop, mood. The system automatically appends "print the customer's design on the &lt;location name&gt;" so you don't need to mention the design.</p>
          <div className="space-y-3">
            {garments.map((g) => <GarmentPromptRow key={g.id} garment={g} onSaved={() => { /* in-place */ }} />)}
          </div>
        </div>
      )}

      {/* Current state summary — quick glance at which garments still need photos. */}
      {garmentsLoaded && (
        <div className="card">
          <div className="eyebrow">AI reference coverage</div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {garments.map((g) => {
              const total = g.printLocations.length
              const filled = g.printLocations.filter((l) => l.aiReferenceFileId).length
              const pct = total ? Math.round((filled / total) * 100) : 0
              return (
                <div key={g.id} className="border border-border rounded p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ink font-semibold">{g.name}</span>
                    <span className={'text-xs font-bold ' + (pct === 100 ? 'text-emerald-700' : pct > 0 ? 'text-gold-dark' : 'text-ink-3')}>{filled}/{total}</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {g.printLocations.map((l) => (
                      <div key={l.id} className="flex-1 flex flex-col items-center" title={l.name}>
                        <div className="w-full h-16 bg-surface border border-border rounded overflow-hidden flex items-center justify-center">
                          {l.aiReferenceUrl ? <img src={apiSrc(l.aiReferenceUrl)} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-ink-3">empty</span>}
                        </div>
                        <span className="text-[10px] text-ink-3 mt-1 truncate w-full text-center">{l.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* AI references — grouped by garment, auto-labelled. Uploaded from
          each garment's detail page via the AI references panel. */}
      {aiRefsLoaded && (
        <div className="card mt-6">
          <div className="eyebrow" style={{ color: '#7E22CE' }}>AI references ({aiRefs.length})</div>
          <p className="text-[11px] text-ink-3 mt-1">Reference photos dropped on each garment's detail page. Future AI-mockup generation will train on this dataset.</p>
          {aiRefs.length === 0 ? (
            <p className="text-sm text-ink-3 mt-3">No AI references uploaded yet. Open a garment's detail page (only visible to rex@ethx.com.au) and drop files into the AI references panel.</p>
          ) : (
            <div className="space-y-5 mt-4">
              {(() => {
                // Group by productId (auto-labelled by garment name).
                const groups = new Map<string, { name: string; items: AiRef[] }>()
                for (const r of aiRefs) {
                  const key = r.productId ?? 'unbound'
                  const label = r.productName ?? 'Unbound'
                  if (!groups.has(key)) groups.set(key, { name: label, items: [] })
                  groups.get(key)!.items.push(r)
                }
                return Array.from(groups.entries()).map(([pid, g]) => (
                  <div key={pid}>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="font-heading uppercase font-bold text-sm text-ink tracking-wider">{g.name}</div>
                      <div className="text-[11px] text-ink-3">{g.items.length} reference{g.items.length === 1 ? '' : 's'}</div>
                    </div>
                    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {g.items.map((r) => (
                        <div key={r.id} className="relative group">
                          <div className="aspect-square bg-surface border border-border rounded overflow-hidden flex items-center justify-center">
                            <img src={apiSrc(r.url)} alt={r.originalName} className="max-w-full max-h-full object-contain" title={r.originalName} />
                          </div>
                          <button onClick={() => removeAiRef(r.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-white/95 border border-border rounded text-ink-3 hover:text-red-600 hover:border-red-300 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs leading-none shadow-sm"
                            title="Remove">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Per-garment prompt row. Saves to /api/admin/garments/:id aiPrompt. Local
// optimistic state so the field is responsive; the save call is one debounced
// click rather than typing-into-server.
function GarmentPromptRow({ garment, onSaved }: { garment: AdminGarment; onSaved: () => void }) {
  const { getAccessToken } = useAuth()
  const [value, setValue] = useState(garment.aiPrompt ?? '')
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle')
  useEffect(() => { setValue(garment.aiPrompt ?? '') }, [garment.aiPrompt])

  async function save() {
    setSaving('saving')
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${garment.id}`, {
        method: 'PATCH', body: JSON.stringify({ aiPrompt: value.trim() || null }),
      })
      setSaving('saved')
      onSaved()
      window.setTimeout(() => setSaving('idle'), 1500)
    } catch (e) { setSaving('idle'); alert((e as Error).message) }
  }

  return (
    <div className="border border-border rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-ink">{garment.name}</div>
          <div className="text-[11px] text-ink-3 font-mono">{garment.slug}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={'text-[11px] ' + (saving === 'saved' ? 'text-emerald-700' : 'text-ink-3')}>
            {saving === 'saving' ? 'Saving…' : saving === 'saved' ? '✓ Saved' : ''}
          </span>
          <button onClick={save} disabled={saving === 'saving' || value === (garment.aiPrompt ?? '')} className="btn-gold text-[11px] px-3 py-1">Save</button>
        </div>
      </div>
      <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3} placeholder="e.g. A fitted heavyweight black cotton t-shirt with crew neck and short sleeves, worn by a relaxed adult model with arms at sides, neutral grey studio backdrop, soft even key light from front-left, sharp focus, photoreal."
        className="input !text-xs font-mono" />
    </div>
  )
}
